import { useEffect, useState, useMemo, useCallback } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Grid, useTheme } from '@mui/material';
import { ServiceClient } from '../../helpers/client';
import { CustomerInfo, ServiceCategory, ServiceGroup } from 'orderme-api-integration-client';
import { ServiceLogoItem } from '../serviceLogoItem/serviceLogoItem';
import { useAppSelector } from '../../redux/hooks';
import { RootState } from '../../redux/store';
import { useRouteNavigator } from '../../routes/useRouteNavigator';

export function ServiceCarousel() {
  const theme = useTheme();
  const [category, setCategory] = useState<ServiceCategory>();
  const routeNavigator = useRouteNavigator();
  const [categoryGroups, setCategoryGroups] = useState<ServiceGroup[]>();
  const servicesClient = useMemo(() => new ServiceClient(), []);
  const customerInfo: CustomerInfo = useAppSelector((state: RootState) => state.customerState.customerInfo);
  const categories: ServiceCategory[] = useAppSelector((state: RootState) => state.categoriesState.categories);

  useEffect(() => {
    if (categories?.length > 0) {
      setCategory(categories[0]);
    }
  }, [categories]);

  const getCategoryGroups = useCallback(() => {
    if (!category?.id) {
      setCategoryGroups(null);
      return;
    }

    servicesClient
      .getCategoryGroups(category.id, customerInfo.id)
      .then((response) => {
        setCategoryGroups(response);
      })
      .catch((err) => {
        setCategoryGroups(null);
        console.log(err);
      });
  }, [category, customerInfo, servicesClient]);

  useEffect(() => {
    getCategoryGroups();
  }, [getCategoryGroups]);

  const showCategoryGroup = (serviceGroup: ServiceGroup) => {
    routeNavigator.goToCategoryGroup(category, serviceGroup);
  };

  return (
    <Grid
      container
      direction="column"
      sx={{
        alignItems: 'center',
        maxWidth: '1500px',
        width: '100%',
        mx: 'auto',
        '& .swiper-button-next, & .swiper-button-prev': {
          mx: '5px',
          backgroundColor: 'rgba(255,255,255,0.7)',
          borderRadius: '50%',
          width: '30px',
          height: '30px',
          padding: '10px',
          color: 'primary.main',
          opacity: 0,
          transition: 'opacity 0.2s ease',
        },
        '&:hover .swiper-button-next, &:hover .swiper-button-prev': {
          opacity: 1,
        },
      }}
    >
      {categoryGroups && categoryGroups.length > 0 && (
        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          slidesPerView={1}
          navigation
          pagination={{ clickable: true }}
          loop={true}
          autoplay={{ delay: 3000, disableOnInteraction: false, pauseOnMouseEnter: true }}
          breakpoints={{
            600: {
              slidesPerView: 2,
            },
            1000: {
              slidesPerView: 3,
            },
          }}
          style={
            {
              width: '100%',
              paddingBottom: '40px',
              '--swiper-pagination-color': theme.palette.primary.main,
            } as React.CSSProperties
          }
        >
          {categoryGroups.map((service) => (
            <SwiperSlide key={service.id}>
              <Grid
                sx={{
                  p: '5px',
                  height: '350px',
                }}
              >
                <ServiceLogoItem
                  service={service}
                  isButton={true}
                  imageWidth={800}
                  open={() => {
                    showCategoryGroup(service);
                  }}
                />
              </Grid>
            </SwiperSlide>
          ))}
        </Swiper>
      )}
    </Grid>
  );
}
